import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumbs from "../../components/Breadcrumbs";
import Disclaimer from "../../components/Disclaimer";
import CalculatorShell, { bandResult } from "./CalculatorShell";
import { Calculator } from "lucide-react";

/* ------------------------------------------------------------------
 * BMI Calculator
 * ------------------------------------------------------------------ */
export const bmiConfig = {
  slug: "bmi",
  title: "BMI Calculator",
  subtitle: "Body Mass Index is a general screening tool relating weight to height. Individual health depends on many additional factors that only a clinician can evaluate.",
  metaDescription: "Educational BMI (Body Mass Index) calculator. Understand what your BMI number means and why it is only a screening measure, not a diagnosis.",
  fields: [
    { id: "unit", label: "Unit system", type: "select", default: "metric",
      options: [
        { value: "metric",   label: "Metric (kg / cm)" },
        { value: "imperial", label: "Imperial (lb / in)" },
      ] },
    { id: "weight", label: "Weight", unit: "kg or lb", type: "number", placeholder: "70", min: 20, max: 400 },
    { id: "height", label: "Height", unit: "cm or in", type: "number", placeholder: "170", min: 50, max: 250 },
  ],
  compute: ({ unit, weight, height }) => {
    let kg = weight, m = height / 100;
    if (unit === "imperial") { kg = weight * 0.453592; m = (height * 2.54) / 100; }
    if (m <= 0) throw new Error("Height must be greater than 0");
    const bmi = kg / (m * m);
    return {
      ...bandResult({
        value: bmi,
        unit: "kg/m²",
        bands: [
          { label: "Underweight",    min: 0,    max: 18.5 },
          { label: "Healthy weight", min: 18.5, max: 25 },
          { label: "Overweight",     min: 25,   max: 30 },
          { label: "Obesity class I",  min: 30, max: 35 },
          { label: "Obesity class II", min: 35, max: 40 },
          { label: "Obesity class III", min: 40, max: 999 },
        ],
        explanation: "BMI does not measure body composition. Athletes and individuals with high muscle mass may have elevated BMI without excess body fat. Older adults may have low BMI without being underweight relative to their functional health.",
      }),
      range: "Adults: healthy 18.5–24.9 kg/m² per WHO/CDC classification.",
    };
  },
  formula: (
    <>
      <p><strong>Formula:</strong> BMI = weight (kg) / height (m)². Imperial inputs are converted using 1 lb = 0.4536 kg and 1 in = 2.54 cm.</p>
      <p>BMI is a screening tool, not a diagnosis. It does not distinguish fat, muscle, or bone. Use it alongside other measures such as waist circumference and clinical evaluation.</p>
    </>
  ),
  relatedArticles: ["understanding-glp-1-medications", "semaglutide-explained", "tirzepatide-explained"],
  relatedGoals: ["weight-loss", "metabolic-health"],
};

/* ------------------------------------------------------------------
 * Basal Metabolic Rate (BMR) — Mifflin-St Jeor
 * ------------------------------------------------------------------ */
export const bmrConfig = {
  slug: "bmr",
  title: "Basal Metabolic Rate (BMR) Calculator",
  subtitle: "BMR estimates the calories your body needs at rest to keep essential functions running. This is an educational estimate — real energy needs vary.",
  metaDescription: "Educational BMR calculator using the Mifflin-St Jeor equation. Estimate resting energy expenditure with a clear explanation of the formula.",
  fields: [
    { id: "sex",    label: "Biological sex", type: "select", default: "female",
      options: [
        { value: "female", label: "Female" },
        { value: "male",   label: "Male" },
      ] },
    { id: "age",    label: "Age", unit: "years", type: "number", placeholder: "35", min: 15, max: 100 },
    { id: "weight", label: "Weight", unit: "kg", type: "number", placeholder: "70", min: 20, max: 300 },
    { id: "height", label: "Height", unit: "cm", type: "number", placeholder: "170", min: 100, max: 250 },
  ],
  compute: ({ sex, age, weight, height }) => {
    // Mifflin-St Jeor
    const s = sex === "male" ? 5 : -161;
    const bmr = (10 * weight) + (6.25 * height) - (5 * age) + s;
    return {
      value: Math.round(bmr),
      unit: "kcal/day",
      explanation: "This is the calories your body needs at complete rest for essential functions (breathing, circulation, cell repair). Total daily needs are higher — see the TDEE calculator to include activity.",
      range: "Most adults land between 1,200–2,000 kcal/day BMR.",
    };
  },
  formula: (
    <>
      <p><strong>Equation:</strong> Mifflin-St Jeor (widely used in clinical settings):</p>
      <p className="font-mono text-xs bg-stone-50 border border-stone-200 rounded p-2">
        BMR = 10·weight(kg) + 6.25·height(cm) − 5·age(y) + <em>s</em><br />
        <em>s</em> = +5 (male) or −161 (female)
      </p>
      <p>The equation was developed for adults ages 19–78 and generally has ±10% accuracy. Body composition, medications, and metabolic conditions can shift real BMR meaningfully.</p>
    </>
  ),
  relatedArticles: ["understanding-glp-1-medications", "how-peptides-work-in-the-body"],
  relatedGoals: ["metabolic-health", "weight-loss"],
};

/* ------------------------------------------------------------------
 * TDEE = BMR × activity factor
 * ------------------------------------------------------------------ */
export const tdeeConfig = {
  slug: "tdee",
  title: "Total Daily Energy Expenditure (TDEE) Calculator",
  subtitle: "TDEE estimates the calories your body uses in a day, including movement and exercise. Use with a clinician to plan meaningful goals.",
  metaDescription: "Educational TDEE calculator (Mifflin-St Jeor × activity multiplier). Understand total daily energy expenditure and its role in weight management.",
  fields: [
    { id: "sex",    label: "Biological sex", type: "select", default: "female",
      options: [{ value: "female", label: "Female" }, { value: "male", label: "Male" }] },
    { id: "age",    label: "Age", unit: "years", type: "number", placeholder: "35", min: 15, max: 100 },
    { id: "weight", label: "Weight", unit: "kg", type: "number", placeholder: "70", min: 20, max: 300 },
    { id: "height", label: "Height", unit: "cm", type: "number", placeholder: "170", min: 100, max: 250 },
    { id: "activity", label: "Activity level", type: "select", default: "1.375",
      options: [
        { value: "1.2",   label: "Sedentary (little/no exercise)" },
        { value: "1.375", label: "Light (1–3 days/week)" },
        { value: "1.55",  label: "Moderate (3–5 days/week)" },
        { value: "1.725", label: "Very active (6–7 days/week)" },
        { value: "1.9",   label: "Athlete / physical job" },
      ] },
  ],
  compute: ({ sex, age, weight, height, activity }) => {
    const s = sex === "male" ? 5 : -161;
    const bmr = (10 * weight) + (6.25 * height) - (5 * age) + s;
    const tdee = bmr * Number(activity);
    return {
      value: Math.round(tdee),
      unit: "kcal/day",
      explanation: "This is a rough estimate of total daily calories. Actual energy needs vary with body composition, thermogenesis, medications, and week-to-week activity. Any meaningful nutrition plan should be built with a licensed clinician.",
      range: "Adults typically range 1,600–3,000 kcal/day depending on size and activity.",
    };
  },
  formula: (
    <>
      <p><strong>Two-step calculation:</strong></p>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Compute BMR using Mifflin-St Jeor.</li>
        <li>Multiply by an activity factor (1.2–1.9) representing weekly movement.</li>
      </ol>
      <p>Activity factors are derived from population studies. Individual metabolism can differ by 15–20%, which is why professional evaluation is important before setting calorie targets.</p>
    </>
  ),
  relatedArticles: ["semaglutide-explained", "tirzepatide-explained", "understanding-glp-1-medications"],
  relatedGoals: ["weight-loss", "metabolic-health"],
};

/* ------------------------------------------------------------------
 * Waist-to-Height Ratio
 * ------------------------------------------------------------------ */
export const waistHeightConfig = {
  slug: "waist-to-height",
  title: "Waist-to-Height Ratio Calculator",
  subtitle: "Waist-to-height ratio is a simple, evidence-supported screening measure that may correlate with cardiometabolic risk better than BMI alone.",
  metaDescription: "Educational waist-to-height ratio (WHtR) calculator. Understand what your ratio means and how it complements BMI for cardiometabolic risk screening.",
  fields: [
    { id: "unit",   label: "Unit system", type: "select", default: "metric",
      options: [{ value: "metric", label: "cm" }, { value: "imperial", label: "inches" }] },
    { id: "waist",  label: "Waist circumference", unit: "cm or in", type: "number", placeholder: "80", min: 20, max: 200 },
    { id: "height", label: "Height", unit: "cm or in", type: "number", placeholder: "170", min: 50, max: 250 },
  ],
  compute: ({ unit, waist, height }) => {
    if (height <= 0) throw new Error("Height must be greater than 0");
    // Ratio unitless — both sides in same units
    const ratio = waist / height;
    return {
      ...bandResult({
        value: ratio,
        unit: "",
        bands: [
          { label: "Low risk (may be underweight)", min: 0,    max: 0.42 },
          { label: "Healthy",                       min: 0.42, max: 0.53 },
          { label: "Increased risk",                min: 0.53, max: 0.58 },
          { label: "High risk",                     min: 0.58, max: 999 },
        ],
        explanation: "Keep your waist to less than half your height is a common heuristic used in adult screening. This is a screening tool only — cardiometabolic risk depends on many factors including blood pressure, lipids, glucose, and family history.",
      }),
      range: "General reference: 0.4–0.5 is considered healthy for most adults.",
    };
  },
  formula: (
    <>
      <p><strong>Formula:</strong> WHtR = waist circumference / height (same units).</p>
      <p>Waist should be measured horizontally at the level of the navel or the narrowest point of the torso, at the end of a normal exhale. Consistent measurement matters more than perfect technique.</p>
    </>
  ),
  relatedArticles: ["semaglutide-explained", "tirzepatide-explained"],
  relatedGoals: ["metabolic-health", "weight-loss"],
};

/* ------------------------------------------------------------------
 * Daily Protein Intake
 * ------------------------------------------------------------------ */
export const proteinConfig = {
  slug: "daily-protein",
  title: "Daily Protein Intake Calculator",
  subtitle: "Estimates daily protein needs based on body weight and goal. Higher intake ranges are used in muscle-preservation and active-aging contexts — always discuss goals with a clinician.",
  metaDescription: "Educational daily protein intake calculator with goal-adjusted ranges (sedentary, active, muscle-preservation, athletic). Explains the g/kg equation.",
  fields: [
    { id: "unit",   label: "Weight unit", type: "select", default: "kg",
      options: [{ value: "kg", label: "kg" }, { value: "lb", label: "lb" }] },
    { id: "weight", label: "Body weight", unit: "kg or lb", type: "number", placeholder: "70", min: 20, max: 400 },
    { id: "goal",   label: "Goal / context", type: "select", default: "active",
      options: [
        { value: "sedentary",     label: "Sedentary adult (0.8 g/kg)" },
        { value: "active",        label: "Active adult (1.2 g/kg)" },
        { value: "preservation",  label: "Muscle preservation / older adult (1.5 g/kg)" },
        { value: "athletic",      label: "Athletic / muscle building (1.8 g/kg)" },
        { value: "weight_loss",   label: "Muscle-sparing during weight loss (1.6 g/kg)" },
      ] },
  ],
  compute: ({ unit, weight, goal }) => {
    const kg = unit === "lb" ? weight * 0.453592 : weight;
    const factors = { sedentary: 0.8, active: 1.2, preservation: 1.5, athletic: 1.8, weight_loss: 1.6 };
    const g = kg * factors[goal];
    return {
      value: Math.round(g),
      unit: "g protein/day",
      explanation: "Individual protein needs depend on kidney function, medications, and total dietary pattern. Older adults, individuals on GLP-1 therapy, and people in muscle-loss risk categories often benefit from the higher end of these ranges — but this should be personalized with a clinician.",
      range: "General adult range: 0.8–1.8 g/kg/day.",
    };
  },
  formula: (
    <>
      <p><strong>Formula:</strong> Protein (g/day) = body weight (kg) × goal factor.</p>
      <p>Goal factors are drawn from ISSN, ACSM, and geriatrics literature. Chronic kidney disease may warrant lower intakes — never override medical guidance.</p>
    </>
  ),
  relatedArticles: ["understanding-glp-1-medications", "semaglutide-explained"],
  relatedGoals: ["muscle-preservation", "weight-loss", "metabolic-health"],
};

export const ALL_CALCULATORS = [bmiConfig, bmrConfig, tdeeConfig, waistHeightConfig, proteinConfig];

/* ------------------------------------------------------------------
 * Directory page /calculators
 * ------------------------------------------------------------------ */
export function CalculatorsIndex() {
  const ORIGIN = "https://medicalpeptides.ai";
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Educational health calculators",
    itemListElement: ALL_CALCULATORS.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${ORIGIN}/calculators/${c.slug}`,
      name: c.title,
    })),
  };
  return (
    <div data-testid="calculators-index">
      <Helmet>
        <title>Educational Health Calculators | Medical Peptides</title>
        <meta name="description" content="Educational calculators for BMI, BMR, TDEE, waist-to-height ratio, and daily protein intake. All results are informational — evaluation requires a licensed clinician." />
        <link rel="canonical" href={`${ORIGIN}/calculators`} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>
      <section className="vv-hero">
        <div className="vv-container py-12">
          <Breadcrumbs items={[{ name: "Calculators", path: "/calculators" }]} />
          <div className="vv-hero-eyebrow mb-2 flex items-center gap-2"><Calculator size={14} /> Educational tools</div>
          <h1 className="font-display text-4xl md:text-5xl">Health Calculators</h1>
          <p className="mt-3 text-stone-600 max-w-3xl">
            Educational tools for exploring body composition, metabolism, and nutrition. Every result is informational — a licensed clinician should interpret them alongside your history, labs, and goals.
          </p>
        </div>
      </section>
      <section className="vv-container py-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {ALL_CALCULATORS.map((c) => (
          <Link
            key={c.slug}
            to={`/calculators/${c.slug}`}
            data-testid={`calculators-index-link-${c.slug}`}
            className="vv-card p-6 hover:border-[color:var(--vv-gold)] transition-colors"
          >
            <div className="text-xs uppercase tracking-widest text-stone-500">{c.title.split(" (")[0]}</div>
            <div className="font-display text-xl mt-1">{c.title}</div>
            <p className="text-sm text-stone-600 mt-2">{c.subtitle}</p>
          </Link>
        ))}
      </section>
      <section className="vv-container pb-10"><Disclaimer /></section>
    </div>
  );
}

/* Convenience components that each render one calculator */
export const BmiCalculator = () => <CalculatorShell config={bmiConfig} />;
export const BmrCalculator = () => <CalculatorShell config={bmrConfig} />;
export const TdeeCalculator = () => <CalculatorShell config={tdeeConfig} />;
export const WaistHeightCalculator = () => <CalculatorShell config={waistHeightConfig} />;
export const ProteinCalculator = () => <CalculatorShell config={proteinConfig} />;
